import React, { useEffect, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import { useTranslation } from 'react-i18next';

const exportFormats = [
  { extension: 'png', label: 'PNG', mimeType: 'image/png' },
  { extension: 'jpg', label: 'JPEG', mimeType: 'image/jpeg' },
  { extension: 'webp', label: 'WebP', mimeType: 'image/webp' },
];

export const CaptureDownloadDialog = ({ open, onClose, onSubmit, getCapture }) => {
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);
  const [viewerBounds, setViewerBounds] = useState(null);
  const [captureBlob, setCaptureBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [filename, setFilename] = useState('capture');
  const [exportFormat, setExportFormat] = useState(exportFormats[0]);
  const [isCapturing, setIsCapturing] = useState(false);
  const previewImageRef = useRef();

  useEffect(() => {
    if (!open) return undefined;

    const margin = 4;
    const updateViewerBounds = () => {
      const viewerLayout = document.querySelector('section.ViewerLayout');
      if (!viewerLayout) return;

      const { left, top, width, height } = viewerLayout.getBoundingClientRect();
      setViewerBounds({
        left: left + margin,
        top: top + margin,
        width: Math.max(width - (margin * 2), 0),
        height: Math.max(height - (margin * 2), 0),
      });
    };

    updateViewerBounds();
    window.addEventListener('resize', updateViewerBounds);
    const viewerLayout = document.querySelector('section.ViewerLayout');
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateViewerBounds);
    if (resizeObserver && viewerLayout) resizeObserver.observe(viewerLayout);

    return () => {
      window.removeEventListener('resize', updateViewerBounds);
      resizeObserver?.disconnect();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    let isCurrent = true;
    let objectUrl;
    setCaptureBlob(null);
    setPreviewUrl(null);
    setImageDimensions(null);
    setFilename('capture');
    setExportFormat(exportFormats[0]);
    setIsCapturing(true);

    getCapture()
      .then((blob) => {
        if (!blob || !isCurrent) return;
        objectUrl = URL.createObjectURL(blob);
        setCaptureBlob(blob);
        setPreviewUrl(objectUrl);
      })
      .catch((error) => console.error('Unable to generate capture preview:', error))
      .finally(() => {
        if (isCurrent) setIsCapturing(false);
      });

    return () => {
      isCurrent = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, getCapture]);

  const createOutputBlob = () => {
    if (!captureBlob || !previewImageRef.current || exportFormat.mimeType === 'image/png') {
      return captureBlob;
    }

    const canvas = document.createElement('canvas');
    canvas.width = imageDimensions.width;
    canvas.height = imageDimensions.height;
    if (exportFormat.mimeType === 'image/jpeg') {
      const context = canvas.getContext('2d');
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    canvas.getContext('2d').drawImage(previewImageRef.current, 0, 0, canvas.width, canvas.height);

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Unable to export capture'));
      }, exportFormat.mimeType, 0.92);
    });
  };

  const handleSubmit = async () => {
    setDisabled(true);
    try {
      const baseFilename = filename.trim() || 'capture';
      await onSubmit(await createOutputBlob(), `${baseFilename}.${exportFormat.extension}`);
      onClose();
    } finally {
      setDisabled(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={disabled ? undefined : onClose}
      aria-labelledby="capture-download-dialog-title"
      maxWidth={false}
      PaperProps={{
        style: {
          width: viewerBounds?.width || 500,
          maxWidth: viewerBounds ? 'none' : 'calc(100% - 24px)',
          height: viewerBounds ? Math.max(viewerBounds.height - 144, 0) : undefined,
          minHeight: viewerBounds ? 0 : 150,
          left: viewerBounds?.left || '50%',
          margin: 0,
          position: 'fixed',
          top: viewerBounds?.top || 75,
          transform: viewerBounds ? 'none' : 'translate(-50%, 0)',
          fontSize: '1.3175rem',
          textAlign: 'center',
          color: '#dfe5ea',
          backgroundColor: '#070707D7',
          border: '2px solid #999',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogContent
        id="capture-download-dialog-title"
        style={{ display: 'flex', flex: 1, minHeight: 0, padding: 16 }}
      >
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            justifyContent: 'center',
            minHeight: 0,
          }}
        >
          {previewUrl && (
            <img
              src={previewUrl}
              alt={t('toolbar.downloadCapture')}
              onLoad={(event) => setImageDimensions({
                height: event.currentTarget.naturalHeight,
                width: event.currentTarget.naturalWidth,
              })}
              ref={previewImageRef}
              style={{
                border: '1px solid #F5F5F51F',
                borderRadius: 6,
                maxHeight: 'calc(100% - 48px)',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          )}
          {imageDimensions && (
            <div style={{ alignItems: 'center', display: 'flex', marginTop: 8, width: '100%' }}>
              <TextField
                InputLabelProps={{ style: { color: '#dfe5ea' } }}
                InputProps={{ style: { color: '#dfe5ea' } }}
                label={t('toolbar.captureFilename')}
                onChange={(event) => setFilename(event.target.value)}
                style={{ width: 180 }}
                value={filename}
                variant="outlined"
              />
              <TextField
                InputLabelProps={{ style: { color: '#dfe5ea' } }}
                InputProps={{ style: { color: '#dfe5ea' } }}
                label={t('toolbar.captureFormat')}
                onChange={(event) => setExportFormat(
                  exportFormats.find((format) => format.mimeType === event.target.value)
                )}
                SelectProps={{
                  MenuProps: {
                    PaperProps: { style: { backgroundColor: '#111C', color: '#dfe5ea' } },
                    style: { zIndex: 1400 },
                  },
                }}
                select
                style={{ marginLeft: 8 }}
                value={exportFormat.mimeType}
                variant="outlined"
              >
                {exportFormats.map((format) => (
                  <MenuItem key={format.mimeType} style={{ color: '#dfe5ea' }} value={format.mimeType}>
                    {format.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                InputLabelProps={{ style: { color: '#878F95' } }}
                InputProps={{ readOnly: true, style: { color: '#878F95' } }}
                label={t('toolbar.captureResolution')}
                style={{ marginLeft: 'auto', width: 160 }}
                value={`${imageDimensions.width} × ${imageDimensions.height} px`}
                variant="outlined"
              />
            </div>
          )}
          {isCapturing && <span>{t('toolbar.downloadCapture')}</span>}
        </div>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center' }}>
        <Button
          variant="outlined"
          disabled={disabled || isCapturing || !captureBlob || !imageDimensions}
          onClick={handleSubmit}
          style={{
            backgroundColor: '#132630',
            border: '1px solid #324b5a',
            color: '#eef3f5',
          }}
        >
          {t('download')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaptureDownloadDialog;
